/**
 * Role-Based Navigation Configuration
 *
 * Defines navigation items with user type access control.
 * Matches the pattern from AIadvisorCopilot.
 *
 * User types are stored in auth_schema.user_types table (uppercase with underscores):
 * - SUPER_ADMIN: Platform Super Admin (manages entire platform)
 * - PLATFORM_ADMIN: Platform Admin (manages platform operations)
 * - CLAIMS_ADJUSTER: Platform Employee (reviews and manages jobs)
 * - FIELD_AGENT: Mobile User (performs inspections)
 * - INSURER_USER: Tenant User (views reports)
 * - INSURER_ADMIN: Tenant Admin (tenant administration)
 * - VERIFIER: Platform Employee (quality checks)
 * - PARTNER: Partner User
 * - VENDOR: Vendor User
 * - GARAGE: Garage User
 */

export interface NavigationItem {
  label: string;
  href?: string | undefined;
  icon: React.ElementType | string; // Will be Lucide icon component or string icon name
  userTypes: string[]; // User type codes that can access this item (from user_types.code column)
  description?: string | undefined;
  permissionCode?: string | undefined; // Optional: specific permission required
  /**
   * Tenant must have this `job_type_code` enabled under Tenant Settings → Use Cases
   * for the item to appear. When omitted, use-case gating is not applied.
   */
  requiredUseCaseCode?: string | undefined;
  submenu?: {
    label: string;
    href: string;
    userTypes?: string[] | undefined; // Optional: override parent userTypes
    permissionCode?: string | undefined;
  }[] | undefined;
}

/**
 * Main navigation items accessible based on user type
 * Filtered in Sidebar/Layout component based on session user's type code
 */
export const navigationItems: NavigationItem[] = [
  {
    label: 'Dashboard',
    href: '/dashboard',
    icon: 'HomeIcon',
    userTypes: [
      'SUPER_ADMIN',
      'PLATFORM_ADMIN',
      'CLAIMS_ADJUSTER',
      'FIELD_AGENT',
      'INSURER_USER',
      'INSURER_ADMIN',
      'VERIFIER',
      'PARTNER',
      'VENDOR',
      'CLAIM_RO',
      // GARAGE intentionally excluded: garage users have no /dashboard
      // data access (stats/analytics/activity all 403 for them by design)
      // and no dedicated dashboard of their own - the Vehicle Claims nav
      // item below is their sole entry point. CLAIM_RO has full stats
      // access (prefix-filtered), unlike Garage, so it's included here.
    ],
    description: 'Dashboard overview',
  },
  {
    label: 'Tenants',
    href: '/super-admin/tenants',
    icon: 'BuildingOfficeIcon',
    userTypes: ['SUPER_ADMIN'], // Only Super Admin
    description: 'Manage tenant organizations',
  },
  {
    label: 'Employees',
    href: '/super-admin/employees',
    icon: 'UserGroupIcon',
    userTypes: ['SUPER_ADMIN'], // Only Super Admin
    description: 'Manage platform employees',
  },
  {
    label: 'Partners',
    href: '/super-admin/partners',
    icon: 'UsersIcon',
    userTypes: ['SUPER_ADMIN', 'PLATFORM_ADMIN'], // Super Admin and Platform Admin
    description: 'Manage partner organizations',
  },
  {
    label: 'Users',
    href: '/users', // Redirects to appropriate page based on user type
    icon: 'UserIcon',
    userTypes: ['SUPER_ADMIN', 'PLATFORM_ADMIN', 'INSURER_ADMIN'], // Admins
    description: 'Manage system users',
  },
  {
    label: 'Jobs',
    href: '/jobs',
    icon: 'BriefcaseIcon',
    userTypes: [
      'SUPER_ADMIN',
      'PLATFORM_ADMIN',
      'CLAIMS_ADJUSTER',
      'FIELD_AGENT',
      'INSURER_USER',
      'INSURER_ADMIN',
      'VERIFIER',
    ],
    description: 'Manage inspection jobs',
  },
  {
    label: 'Pre-Inspections',
    href: '/pre-inspections',
    icon: 'ClipboardDocumentCheckIcon',
    userTypes: [
      'SUPER_ADMIN',
      'PLATFORM_ADMIN',
      'CLAIMS_ADJUSTER',
      'VERIFIER',
      'INSURER_USER',
      'INSURER_ADMIN',
    ],
    requiredUseCaseCode: 'PRE_INSPECTION',
    description: 'Pre-insurance vehicle condition verification',
  },
  {
    label: 'Vehicle Claims',
    href: '/vehicle-claims',
    icon: 'DocumentTextIcon',
    userTypes: [
      'SUPER_ADMIN',
      'PLATFORM_ADMIN',
      'CLAIMS_ADJUSTER',
      'INSURER_USER',
      'INSURER_ADMIN',
      'CLAIMS_OFFICER',
      'MANAGER',
      'DATA_ENTRY_OPERATOR',
      'TPA_ADMIN',
      'GARAGE',
      // Claim Office -- same restricted (Photos & Videos / Documents
      // only) access as Garage, across every claim in the tenant. Not
      // in the New Claim/Dashboard submenu overrides below, same as Garage.
      'CLAIM_OFFICE',
      // Claim RO -- full staff access, but only to claims whose claim_number
      // starts with one of the user's assigned prefixes (enforced
      // server-side in vehicle_claims.py). Not in the New Claim submenu
      // override below: claim creation isn't prefix-scoped yet (Stage 2).
      'CLAIM_RO',
    ],
    requiredUseCaseCode: 'VEHICLE_CLAIM',
    description: 'Motor insurance claims from intimation to settlement',
    submenu: [
      { label: 'All Claims', href: '/vehicle-claims' },
      {
        label: 'New Claim',
        href: '/vehicle-claims/new',
        // Garage/Claim Office users only ever see already-intimated claims.
        userTypes: [
          'SUPER_ADMIN',
          'PLATFORM_ADMIN',
          'CLAIMS_ADJUSTER',
          'INSURER_USER',
          'INSURER_ADMIN',
          'CLAIMS_OFFICER',
          'MANAGER',
          'DATA_ENTRY_OPERATOR',
          'TPA_ADMIN',
        ],
      },
      {
        label: 'Dashboard',
        href: '/vehicle-claims/dashboard',
        userTypes: [
          'SUPER_ADMIN',
          'PLATFORM_ADMIN',
          'CLAIMS_ADJUSTER',
          'INSURER_USER',
          'INSURER_ADMIN',
          'CLAIMS_OFFICER',
          'MANAGER',
          'DATA_ENTRY_OPERATOR',
          'TPA_ADMIN',
        ],
      },
    ],
  },
  {
    label: 'Insurance Distribution',
    href: '/super-admin/motor-insurance/insurance-companies',
    icon: 'BuildingOfficeIcon',
    userTypes: ['SUPER_ADMIN'],
    requiredUseCaseCode: 'INSURANCE_DISTRIBUTION',
    description: 'Motor insurance distribution through Sub-Agent (dealer) network',
    submenu: [
      { label: 'Insurance Companies',  href: '/super-admin/motor-insurance/insurance-companies' },
      { label: 'Products',             href: '/super-admin/motor-insurance/products' },
      { label: 'Document Checklist',   href: '/super-admin/motor-insurance/document-checklist' },
      { label: 'Insurer Commission Rates', href: '/super-admin/motor-insurance/insurer-commission-rates' },
      { label: 'Sub-Agents (Dealers)', href: '/super-admin/motor-insurance/sub-agents' },
      { label: 'Sub-Agent Commission Rates', href: '/super-admin/motor-insurance/sub-agent-commission-rates' },
      { label: 'Back-Office Users', href: '/super-admin/motor-insurance/back-office-users' },
      { label: 'Dealer Mapping', href: '/super-admin/motor-insurance/dealer-mapping' },
      { label: 'Commission Ledger', href: '/super-admin/motor-insurance/commission-ledger' },
      { label: 'Insurer Reconciliation', href: '/super-admin/motor-insurance/insurer-reconciliation' },
      { label: 'Reports', href: '/super-admin/motor-insurance/reports' },
    ],
  },
  {
    label: 'My Requests',
    href: '/sub-agent/quote-requests',
    icon: 'DocumentTextIcon',
    userTypes: ['SUB_AGENT_ADMIN', 'SUB_AGENT_USER'],
    requiredUseCaseCode: 'INSURANCE_DISTRIBUTION',
    description: 'Create and track your motor insurance quote requests',
    submenu: [
      { label: 'My Requests', href: '/sub-agent/quote-requests' },
      { label: 'New Request', href: '/sub-agent/quote-requests/new' },
      { label: 'Bulk Upload', href: '/sub-agent/quote-requests/bulk-upload' },
    ],
  },
  {
    label: 'Reports',
    href: '/sub-agent/reports',
    icon: 'DocumentTextIcon',
    userTypes: ['SUB_AGENT_ADMIN', 'SUB_AGENT_USER'],
    requiredUseCaseCode: 'INSURANCE_DISTRIBUTION',
    description: 'View your dealer scorecard and renewal pipeline reports',
  },
  {
    label: 'Workbench',
    href: '/back-office/queue',
    icon: 'BriefcaseIcon',
    userTypes: ['BACK_OFFICE'],
    requiredUseCaseCode: 'INSURANCE_DISTRIBUTION',
    description: 'Review and quote Sub-Agent quote requests assigned to you',
  },
  {
    label: 'Reports',
    href: '/reports',
    icon: 'DocumentTextIcon',
    userTypes: [
      'SUPER_ADMIN',
      'PLATFORM_ADMIN',
      'CLAIMS_ADJUSTER',
      'INSURER_USER',
      'INSURER_ADMIN',
      'VERIFIER',
    ],
    description: 'View and generate reports',
  },
  {
    label: 'Billing Summary',
    href: '/billing-summary',
    icon: 'WalletIcon',
    userTypes: [
      // FINANCER's only screen -- deliberately excluded from Vehicle
      // Claims and everything else. Financer-only for now (no admin
      // oversight role) per direct instruction -- tenant admins were
      // seeing this in their own nav and shouldn't.
      'FINANCER',
    ],
    requiredUseCaseCode: 'VEHICLE_CLAIM',
    description: 'Track invoice payment status for claims with a billing state set',
  },
  {
    label: 'Analytics',
    href: '/analytics',
    icon: 'ChartBarIcon',
    userTypes: ['SUPER_ADMIN', 'PLATFORM_ADMIN', 'CLAIMS_ADJUSTER', 'INSURER_ADMIN'],
    description: 'Analytics and insights',
  },
  {
    label: 'Valuations',
    href: '/evaluator',
    icon: 'CurrencyRupeeIcon',
    userTypes: ['SUPER_ADMIN', 'PLATFORM_ADMIN', 'CLAIMS_ADJUSTER', 'INSURER_ADMIN', 'INSURER_USER'],
    description: 'AI vehicle valuations',
    submenu: [
      { label: 'All Evaluations', href: '/evaluator' },
      { label: 'New Evaluation', href: '/evaluator/new' },
      { label: 'Dealer Network', href: '/evaluator/dealers' },
    ],
  },
];

/**
 * Configuration/Admin navigation items
 * Shown in a separate "Configuration" section
 */
export const configurationItems: NavigationItem[] = [
  {
    label: 'Platform Settings',
    href: '/super-admin/settings',
    icon: 'CogIcon',
    userTypes: ['SUPER_ADMIN'],
    description: 'Platform configuration',
  },
  {
    label: 'Roles & Permissions',
    icon: 'ShieldIcon',
    userTypes: ['SUPER_ADMIN'],
    submenu: [
      {
        label: 'User Types',
        href: '/super-admin/config/user-types',
      },
      {
        label: 'Permissions',
        href: '/super-admin/config/permissions',
      },
      {
        label: 'Type Permissions',
        href: '/super-admin/config/type-permissions',
      },
    ],
  },
  {
    label: 'Workflows',
    icon: 'WorkflowIcon',
    userTypes: ['SUPER_ADMIN', 'PLATFORM_ADMIN'],
    submenu: [
      {
        label: 'Job Workflows',
        href: '/config/workflows/jobs',
      },
      {
        label: 'Approval Rules',
        href: '/config/workflows/approvals',
      },
    ],
  },
  {
    label: 'Lookup Tables',
    href: '/super-admin/lookups',
    icon: 'TableIcon',
    userTypes: ['SUPER_ADMIN'],
    description: 'Manage system lookup tables and reference data',
  },
  {
    label: 'Rate Master',
    href: '/super-admin/rate-master',
    icon: 'DocumentTextIcon',
    userTypes: ['SUPER_ADMIN'],
    description: 'Configure repair and replacement cost rates',
  },
  {
    label: 'Depreciation',
    href: '/super-admin/depreciation',
    icon: 'ChartBarIcon',
    userTypes: ['SUPER_ADMIN'],
    description: 'Configure part and age-wise depreciation percentages per company and product',
  },
  {
    label: 'AI Configuration',
    icon: 'CogIcon',
    userTypes: ['SUPER_ADMIN'],
    submenu: [
      {
        label: 'AI Prompts',
        href: '/super-admin/prompts',
      },
      {
        label: 'Field Definitions',
        href: '/super-admin/field-definitions',
      },
      {
        label: 'Document Types',
        href: '/super-admin/document-types',
      },
      {
        label: 'Field Transformation Rules',
        href: '/super-admin/field-transformations',
      },
      {
        label: 'Prompt Configuration',
        href: '/super-admin/ai-config',
      },
    ],
  },
];

/**
 * Helper function to check if user has access to a menu item
 * @param userTypeCode - User's type code from session (e.g., "SUPER_ADMIN")
 * @param allowedTypes - Array of user type codes allowed for this item
 * @returns true if user has access
 */
export function hasAccess(
  userTypeCode: string | undefined | null,
  allowedTypes: string[]
): boolean {
  if (!userTypeCode) return false;

  // Convert to uppercase and normalize
  const normalizedCode = userTypeCode.toUpperCase().trim();
  const normalizedAllowed = allowedTypes.map(t => t.toUpperCase().trim());

  return normalizedAllowed.includes(normalizedCode);
}

/**
 * Filter navigation items based on user's type code (and optionally tenant use cases)
 * @param items - Navigation items to filter
 * @param userTypeCode - User's type code from session
 * @param enabledUseCases - Optional Set / Array of enabled `job_type_code` values for the
 *   current tenant. When omitted (undefined), use-case gating is skipped entirely — this
 *   preserves existing behaviour for callers that don't yet know the tenant scope (e.g.
 *   SuperAdminLayout, PersonaSidebar).
 * @returns Filtered navigation items
 */
export function getVisibleNavItems(
  items: NavigationItem[],
  userTypeCode: string | undefined | null,
  enabledUseCases?: Set<string> | string[]
): NavigationItem[] {
  const useCaseSet =
    enabledUseCases === undefined
      ? undefined
      : enabledUseCases instanceof Set
        ? enabledUseCases
        : new Set(enabledUseCases);

  const passesUseCaseGate = (item: NavigationItem): boolean => {
    if (!item.requiredUseCaseCode) return true;
    if (useCaseSet === undefined) return true; // unscoped — defer to route-level gate
    return useCaseSet.has(item.requiredUseCaseCode);
  };

  return items
    .filter(item => hasAccess(userTypeCode, item.userTypes) && passesUseCaseGate(item))
    .map(item => {
      // Filter submenu items if present
      if (item.submenu) {
        const visibleSubmenu = item.submenu.filter(subItem =>
          !subItem.userTypes || hasAccess(userTypeCode, subItem.userTypes)
        );
        return {
          ...item,
          submenu: visibleSubmenu.length > 0 ? visibleSubmenu : undefined,
        };
      }
      return item;
    });
}

/**
 * Get navigation items for specific user type
 * Convenience function that combines filtering of both main and config items
 */
export function getNavigationForUserType(userTypeCode: string | undefined | null) {
  return {
    main: getVisibleNavItems(navigationItems, userTypeCode),
    config: getVisibleNavItems(configurationItems, userTypeCode),
  };
}

/**
 * Check if a path is active based on current pathname
 */
export function isPathActive(pathname: string, href: string): boolean {
  // Exact match for dashboard/home pages
  if (href === '/dashboard' || href.endsWith('/dashboard')) {
    return pathname === href;
  }
  // Prefix match for other pages
  return pathname.startsWith(href);
}

/**
 * Map user type codes to their dashboard routes
 * Used for redirecting users after login
 */
export const dashboardRoutes: Record<string, string> = {
  SUPER_ADMIN: '/super-admin/dashboard',
  PLATFORM_ADMIN: '/platform/dashboard',
  CLAIMS_ADJUSTER: '/dashboard',
  FIELD_AGENT: '/field-agent/dashboard',
  INSURER_USER: '/dashboard',
  INSURER_ADMIN: '/dashboard',
  VERIFIER: '/verifier/dashboard',
  PARTNER: '/partner/dashboard',
  VENDOR: '/vendor/dashboard',
  GARAGE: '/garage/dashboard',
  // Financer's sole screen -- there's no dashboard to redirect to.
  FINANCER: '/billing-summary',
};

/**
 * Get dashboard route for user type
 */
export function getDashboardRoute(userTypeCode: string | undefined | null): string {
  if (!userTypeCode) return '/dashboard';
  const normalizedCode = userTypeCode.toUpperCase().trim();
  return dashboardRoutes[normalizedCode] || '/dashboard';
}
